import type { Dispatch } from '@reduxjs/toolkit';
import type { SceneJsonDto } from '@seddi/scene';
import { SurfaceMaterialModel, TextureColorSpace, TextureFormat } from '@seddi/surface-material';
import type { LogoDesc, SceneDesc } from '@seddi/ui-three-viewer';
import { type PieceLogo, PieceSide } from '@seddi/scene';
import { produce } from 'immer';

import { setScene, updateScene } from '../store/appSlice';
import { importScene } from '../viewer/importScene';
import type { StyleAggregatorData, StyleLogoRegion } from '../api/styleAggregator';
import type { MockupLogo } from '../types/mockupLogos';

export const setSceneFromJson = async (dispatch: Dispatch, sceneDto: SceneJsonDto) => {
  const scene = await importScene(sceneDto);
  setScene(scene);
  dispatch(updateScene());
};

const createLogoResource = (
  id: string,
  imageSrc: string,
  size: { x: number; y: number },
): LogoDesc => {
  return {
    id,
    materials: [
      {
        id,
        model: SurfaceMaterialModel.DISNEY,
        params: {
          baseColorMap: {
            id,
            href: imageSrc,
            colorSpace: TextureColorSpace.GAMMA,
            format: TextureFormat.PNG,
          },
          roughness: 0.3,
          specular: 0.8,
        },
      },
    ],
    name: id,
    size,
    _id: id,
    image: imageSrc,
  } as LogoDesc;
};

const createPieceNameToEntityDict = (scene: SceneDesc): Record<string, string> => {
  const dict: Record<string, string> = {};

  for (const e of scene.children as any[]) {
    if (e.components?.garmentPiece) {
      dict[e.name] = e._id;
    }

    if (e.components?.model && (scene as any).models) {
      const modelId = e.components.model.model;
      const model = (scene as any).models.find((m: any) => m.id === modelId);
      const meshDecals: string[] | undefined = model?.meta?.meshDecals;
      if (meshDecals) {
        for (const name of meshDecals) {
          dict[name] = e._id;
        }
      }
    }
  }

  return dict;
};

const addMockupLogosToScene = async (
  scene: SceneDesc,
  styleRegions: StyleLogoRegion[],
  logos: MockupLogo[],
): Promise<SceneDesc> => {
  if (!logos.length) {
    return scene;
  }
  return produce(scene, draft => {
    const pieceNameToEntityId = createPieceNameToEntityDict(draft as SceneDesc);

    (draft as any).logos ??= [];

    for (const logo of logos) {
      const regionState = styleRegions.find(r => r.id === logo.region);
      if (!regionState) {
        continue;
      }

      const entityId = pieceNameToEntityId[regionState.pieceName];
      if (!entityId) {
        continue;
      }

      const entity = (draft.children as any[]).find(e => e._id === entityId);
      if (!entity) {
        continue;
      }

      const resourceId = logo.recoloredId ? logo.recoloredId : logo.id;
      const imageSrc = logo.recoloredImageSrc
        ? logo.recoloredImageSrc
        : logo.imageSrc
          ? logo.imageSrc
          : undefined;
      if (!imageSrc) {
        continue;
      }
      const resource = createLogoResource(resourceId, imageSrc, logo.size);

      (draft as any).logos.push(resource);

      const piece = {
        _id: logo.id,
        meshId: regionState.pieceName,
        resource: resourceId,
        size: logo.size,
        side: regionState?.side || PieceSide.FRONT,
        technique: logo.technique,
        transform: logo.transform as any,
        pivot: { x: 0, y: 0 },
        name: logo.displayName ?? logo.name,
      } as unknown as PieceLogo;

      if (entity.components.garmentPiece) {
        entity.components.garmentPiece.logos = entity.components.garmentPiece.logos ?? [];
        entity.components.garmentPiece.logos.push(piece);
      }
      if ((entity.components as any).model) {
        (entity.components as any).model.logos = (entity.components as any).model.logos ?? [];
        (entity.components as any).model.logos.push(piece);
      }
    }
  });
};

export const setSceneFromStyleAggregatorData = async (
  dispatch: Dispatch,
  data: StyleAggregatorData,
  hideTearAwayLabel: boolean,
) => {
  let scene = await importScene(data.scene);

  if (hideTearAwayLabel) {
    scene = produce(scene, draft => {
      draft.children.forEach(child => {
        if (child.components?.garmentPiece?.logos) {
          child.components.garmentPiece.logos = child.components.garmentPiece.logos.filter(
            logo => !logo.isTearAwayLabel,
          );
        }
      });
    });
  }

  const logos =
    (data.mockup?.liveblocksData?.editorSceneLogos?.logos as MockupLogo[] | undefined) ?? [];

  const sceneWithLogos = await addMockupLogosToScene(scene, data.styleRegions, logos);

  setScene(sceneWithLogos);
  dispatch(updateScene());
};
